import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Wallet, RotateCcw, ArrowRightLeft } from "lucide-react";
import { DragonIcon } from "@/components/DragonIcon";

const DEP_STATUS_BADGE = {
  "Aktif": "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300",
  "Sebagian": "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300",
  "Terpakai": "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950 dark:text-sky-300",
  "Direfund": "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300",
  "Selesai": "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-900 dark:text-slate-300",
};

export default function Deposits() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [opts, setOpts] = useState(null);
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");

  const [useFor, setUseFor] = useState(null);
  const [useForm, setUseForm] = useState({ target_order_id: "", amount: "" });
  const [targets, setTargets] = useState([]);

  const [refundFor, setRefundFor] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: "", account: "Bank BCA" });
  const [refundProof, setRefundProof] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);

  const load = () => {
    api.get(`/deposits?status=${tab}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/deposits/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { api.get("/meta/options").then((r) => setOpts(r.data)); }, []);

  const q = search.trim().toLowerCase();
  const filtered = q ? (rows || []).filter((d) => (d.customer_name || "").toLowerCase().includes(q) || (d.source_order_no || "").toLowerCase().includes(q)) : (rows || []);

  const openUse = (d) => {
    setUseFor(d); setUseForm({ target_order_id: "", amount: String(d.balance) }); setTargets([]);
    api.get(`/deposits/${d.id}/target-orders`).then((r) => setTargets(r.data)).catch(() => {});
  };
  const submitUse = async () => {
    if (!useForm.target_order_id) { toast.error("Pilih order tujuan"); return; }
    if (!useForm.amount) { toast.error("Isi nominal"); return; }
    try {
      await api.post(`/deposits/${useFor.id}/use`, { target_order_id: useForm.target_order_id, amount: parseFloat(useForm.amount) });
      toast.success("Titipan dipakai pada order tujuan");
      setUseFor(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openRefund = (d) => { setRefundFor(d); setRefundForm({ amount: String(d.balance), account: d.account || "Kas Tunai" }); setRefundProof(""); };
  const uploadRefundProof = async (file) => {
    if (!file) return;
    setRefundBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload/payment-proof", fd);
      setRefundProof(r.data.path); toast.success("Bukti refund terunggah");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengunggah"); }
    finally { setRefundBusy(false); }
  };
  const submitRefund = async () => {
    if (!refundForm.amount) { toast.error("Isi nominal refund"); return; }
    if (!refundProof) { toast.error("Bukti refund wajib"); return; }
    try {
      await api.post(`/deposits/${refundFor.id}/refund`, { amount: parseFloat(refundForm.amount), account: refundForm.account, proof_image: refundProof });
      toast.success("Refund titipan dicatat");
      setRefundFor(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!rows) return <PageSkeleton stats={3} rows={5} />;

  const cards = [
    { label: "Saldo Titipan Aktif", value: summary?.total_active || 0, icon: DragonIcon, tone: ["bg-emerald-100 dark:bg-emerald-950", "text-emerald-600"] },
    { label: "Total Dipakai", value: summary?.total_used || 0, icon: ArrowRightLeft, tone: ["bg-sky-100 dark:bg-sky-950", "text-sky-600"] },
    { label: "Total Direfund", value: summary?.total_refunded || 0, icon: RotateCcw, tone: ["bg-rose-100 dark:bg-rose-950", "text-rose-600"] },
  ];

  return (
    <div className="space-y-5">
      <div data-testid="deposits-header">
        <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2">
          <DragonIcon className="h-5 w-5 text-emerald-600" /> Titipan Pelanggan
        </h2>
        <p className="text-xs sm:text-sm text-muted-foreground">Saldo uang pelanggan dari pembatalan order. Bisa dipakai untuk order lain atau dikembalikan (refund).</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-4" data-testid={`dep-stat-${c.label}`}>
            <div className="flex items-center gap-3">
              <div className={`grid place-items-center h-10 w-10 rounded-xl ${c.tone[0]}`}><c.icon className={`h-5 w-5 ${c.tone[1]}`} /></div>
              <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{c.label}</p>
                <p className="font-heading text-base font-extrabold">{formatIDR(c.value)}</p></div>
            </div>
          </Card>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
        <div className="flex gap-1.5">
          {[{ id: "active", label: "Aktif" }, { id: "done", label: "Selesai" }, { id: "all", label: "Semua" }].map((t) => (
            <Button key={t.id} size="sm" variant={tab === t.id ? "gold" : "outline"} className="h-8 text-xs" onClick={() => setTab(t.id)} data-testid={`dep-tab-${t.id}`}>{t.label}</Button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="dep-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari pelanggan atau no. order asal..." className="pl-9 h-9" />
        </div>
      </div>

      <Card className="divide-y divide-border">
        {filtered.length === 0 && <div className="text-center text-muted-foreground py-12 text-sm" data-testid="dep-empty">Tidak ada titipan.</div>}
        {filtered.map((d) => (
          <div key={d.id} data-testid={`dep-row-${d.id}`} className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">{d.customer_name || "-"}</span>
                <Badge variant="outline" className={`text-[10px] ${DEP_STATUS_BADGE[d.status] || ""}`} data-testid={`dep-status-${d.id}`}>{d.status}</Badge>
                <span className="text-[11px] text-muted-foreground">dari order {d.source_order_no}</span>
              </div>
              <div className="flex gap-3 flex-wrap text-[11px] text-muted-foreground mt-1">
                <span>Asal: <b className="text-foreground">{formatIDR(d.original_amount)}</b></span>
                <span>Dipakai: {formatIDR(d.used_amount || 0)}</span>
                <span>Refund: {formatIDR(d.refunded_amount || 0)}</span>
                <span>{formatDate(d.created_at)}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <p className="text-[10px] uppercase text-muted-foreground">Saldo</p>
                <p className="font-heading font-extrabold text-emerald-600" data-testid={`dep-balance-${d.id}`}>{formatIDR(d.balance)}</p>
              </div>
              {d.balance > 0.5 && (
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => openUse(d)} data-testid={`dep-use-${d.id}`}><Wallet className="h-3.5 w-3.5" /> Pakai</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-rose-600" onClick={() => openRefund(d)} data-testid={`dep-refund-${d.id}`}><RotateCcw className="h-3.5 w-3.5" /> Refund</Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={!!useFor} onOpenChange={(o) => !o && setUseFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Pakai Titipan</DialogTitle></DialogHeader>
          {useFor && (
            <div className="space-y-3 py-1">
              <p className="text-sm text-muted-foreground">{useFor.customer_name} — saldo <b className="text-foreground">{formatIDR(useFor.balance)}</b></p>
              <div className="space-y-1.5">
                <Label>Order Tujuan</Label>
                <Select value={useForm.target_order_id} onValueChange={(v) => setUseForm({ ...useForm, target_order_id: v })}>
                  <SelectTrigger data-testid="dep-use-target"><SelectValue placeholder="Pilih order belum lunas" /></SelectTrigger>
                  <SelectContent>
                    {targets.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Tidak ada order aktif belum lunas.</div>}
                    {targets.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_no} · {o.category} · sisa {formatIDR(o.remaining)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Nominal (Rp)</Label><Input data-testid="dep-use-amount" type="number" value={useForm.amount} onChange={(e) => setUseForm({ ...useForm, amount: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUseFor(null)}>Batal</Button>
            <Button onClick={submitUse} variant="gold" data-testid="dep-use-submit">Pakai Titipan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!refundFor} onOpenChange={(o) => !o && setRefundFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Refund Titipan</DialogTitle></DialogHeader>
          {refundFor && (
            <div className="space-y-3 py-1">
              <p className="text-sm text-muted-foreground">{refundFor.customer_name} — saldo <b className="text-foreground">{formatIDR(refundFor.balance)}</b></p>
              <div className="space-y-1.5"><Label>Nominal Refund (Rp)</Label><Input data-testid="dep-refund-amount" type="number" value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Dari Rekening</Label>
                <Select value={refundForm.account} onValueChange={(v) => setRefundForm({ ...refundForm, account: v })}>
                  <SelectTrigger data-testid="dep-refund-account"><SelectValue /></SelectTrigger>
                  <SelectContent>{opts?.payment_methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Bukti Refund <span className="text-rose-600">(wajib)</span></Label>
                <Input data-testid="dep-refund-proof" type="file" accept="image/*" className="text-xs" onChange={(e) => uploadRefundProof(e.target.files?.[0])} />
                {refundBusy && <p className="text-[11px] text-muted-foreground">Mengunggah...</p>}
                {refundProof && !refundBusy && <p className="text-[11px] text-emerald-600 font-medium" data-testid="dep-refund-proof-ok">Bukti terlampir ✓</p>}
              </div>
              <p className="text-[11px] text-muted-foreground">Refund mengurangi saldo kas/bank.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundFor(null)}>Batal</Button>
            <Button onClick={submitRefund} variant="gold" disabled={!refundProof || refundBusy} data-testid="dep-refund-submit">Simpan Refund</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
